import { useState, useCallback, useEffect } from 'react';
import { SearchBox } from '@fluentui/react-components';
import type { SearchBoxChangeEvent, InputOnChangeData } from '@fluentui/react-components';
import { SearchRegular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { useSearchBarStyles } from './SearchBar.styles';

export interface SearchBarProps {
  placeholder?: string;
  /**
   * Controlled value. When provided, the SearchBar is fully controlled by the
   * parent and its internal state is kept in sync via `useEffect`. When
   * omitted, the SearchBar manages its own state (uncontrolled mode).
   */
  value?: string;
  onChange?: ((value: string) => void) | undefined;
}

export function SearchBar({
  placeholder,
  value: controlledValue,
  onChange,
}: SearchBarProps): React.JSX.Element {
  const styles = useSearchBarStyles();
  const { t } = useTranslation();
  const [internalValue, setInternalValue] = useState(controlledValue ?? '');

  // Keep internal state in sync when used as a controlled component.
  useEffect(() => {
    if (controlledValue !== undefined && controlledValue !== internalValue) {
      setInternalValue(controlledValue);
    }
    // We intentionally omit `internalValue` from deps — we only want to react
    // to parent-driven changes, not echo our own setState back.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controlledValue]);

  const handleChange = useCallback(
    (_event: SearchBoxChangeEvent, data: InputOnChangeData) => {
      const newValue = data.value;
      setInternalValue(newValue);
      onChange?.(newValue);
    },
    [onChange]
  );

  const handleDismiss = useCallback(() => {
    setInternalValue('');
    onChange?.('');
  }, [onChange]);

  return (
    <div className={styles.root}>
      <SearchBox
        className={styles.searchBox}
        appearance="filled-lighter"
        placeholder={placeholder ?? t('header.searchPlaceholder')}
        contentBefore={<SearchRegular className={styles.searchIcon} />}
        value={internalValue}
        onChange={handleChange}
        dismiss={{ onClick: handleDismiss }}
      />
    </div>
  );
}
